<script setup lang="ts">
import { GpBtn, GpTextInput } from '@gportal/uikit';
import BillingProfileCard from '@panel/components/app/BillingProfileCard.vue';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { onMounted, reactive, ref, watchEffect } from 'vue';
import AddProjectBillingProfileModal from './AddProjectBillingProfileModal.vue';
import type { BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { create } from '@bufbuild/protobuf';
import { CreateProjectRequestSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';

const isFormValid = defineModel<boolean>('isFormValid', {
  default: false,
});

const { hideCreateButton = false } = defineProps<{
  hideCreateButton?: boolean;
}>();

const emit = defineEmits<{
  created: [projectId: string];
}>();

const project = reactive<{
  name: string;
  billingAddressId: string;
}>({
  name: '',
  billingAddressId: '',
});

watchEffect(() => {
  isFormValid.value = project.name !== '' && project.billingAddressId !== '';
});

const projectStore = useProjectStore();
const { addNotification } = useNotifications();
const isCreatingProject = ref(false);
const onSubmit = async () => {
  const newProject = create(CreateProjectRequestSchema, project);
  isCreatingProject.value = true;
  try {
    const p = await projectStore.createProject(newProject);
    projectStore.changeProject(p.project.id);
    emit('created', p.project.id);
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isCreatingProject.value = false;
  }
};

const showAddBillingProfileModal = ref(false);
const selectBillingProfile = (id: string) => {
  project.billingAddressId = id;
};

const userStore = useUserStore();

const handleBillingProfileCreated = async (id: string) => {
  showAddBillingProfileModal.value = false;
  await userStore.reloadBillingProfiles();
  selectBillingProfile(id);
};

onMounted(() => {
  userStore.reloadBillingProfiles();
});

defineExpose({
  createProject: onSubmit,
});
</script>

<template>
  <form @submit.prevent="onSubmit">
    <GpTextInput
      class="tw-mb-4"
      input-id="create-project-name"
      v-model="project.name"
      :required="true"
      label="Project Name"
      name="name"
      transparent
    />

    <label class="tw-mb-2 tw-text-sm tw-font-bold"> Billing profile </label>
    <!-- prettier-ignore -->
    <BillingProfileCard
      v-for="(billingProfile, index) in userStore.billingProfiles"
      class="tw-mb-4"
      v-playwright="`billing-profile-card_${index}`"
      :key="billingProfile.id"
      :billing-profile="(billingProfile as BillingProfile)"
      :selected="billingProfile.id === project.billingAddressId"
      @click="selectBillingProfile(billingProfile.id)"
    />

    <GpBtn
      class="tw-w-full"
      :icon="['fas', 'fa-plus']"
      label="Add Billing Profile"
      outline
      @click="showAddBillingProfileModal = true"
    />

    <GpBtn
      v-if="!hideCreateButton"
      class="tw-mt-8 tw-w-full"
      :disabled="!isFormValid || isCreatingProject"
      type="submit"
      v-playwright="'create-project-btn'"
    >
      Create
    </GpBtn>

    <AddProjectBillingProfileModal
      :show="showAddBillingProfileModal"
      @close="showAddBillingProfileModal = false"
      @created="handleBillingProfileCreated"
    />
  </form>
</template>

<style scoped></style>
